// =============================================================================
// Portal "My Designs" Page — merchant library of apparel designs
// -----------------------------------------------------------------------------
// One row per MERCHANT_PRODUCT_LIBRARY design (no bound rows, no storefront
// designs). Each card shows the library preview thumbnail, title, bound UPT,
// "Spawned N products" badge, and last edit date.
//
// Data source: GET /api/v1/designs?context=MERCHANT_PRODUCT_LIBRARY
//   • Org-scoped server-side via tenantId.
//   • Page-scoped groupBy on `sourceLibraryDesignId` enriches every row
//     with a `spawnSummary` so we don't N+1 query for badges.
//
// Polling is intentionally MINIMAL — library previews are produced by a
// dedicated worker (apparel-library-preview) that emits a single thumbnail
// per design and rarely re-runs. We refetch on focus and keep the data
// fresh enough; no aggressive polling.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { Badge, Button, Card, Input, Select, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';

interface SpawnSummary {
  productCount: number;
  lastSpawnedAt: string | null;
}

interface LibraryDesign {
  id: string;
  designRefId: string | null;
  title: string | null;
  status: string;
  previewUrl: string | null;
  context: string;
  uptId: string | null;
  upt: { id: string; name: string; slug: string } | null;
  spawnSummary: SpawnSummary | null;
  createdAt: string;
  updatedAt: string;
}

interface DesignsResponse {
  data: LibraryDesign[];
  total: number;
  limit: number;
  offset: number;
}

interface PortalUpt {
  id: string;
  name: string;
  slug: string;
}

const PAGE_LIMIT = 20;

export function MyDesignsPage() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const offset = Math.max(Number(searchParams.get('offset') ?? '0'), 0);
  const search = searchParams.get('search') ?? '';
  const uptId = searchParams.get('uptId') ?? '';

  const [searchInput, setSearchInput] = useState(search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      // Filter changes always reset pagination to the first page.
      if (key !== 'offset') next.delete('offset');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterRef.current('search', searchInput);
      searchDebounceRef.current = null;
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const designsQuery = useQuery({
    queryKey: ['portal', 'my-designs', { offset, search, uptId }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('context', 'MERCHANT_PRODUCT_LIBRARY');
      params.set('limit', String(PAGE_LIMIT));
      params.set('offset', String(offset));
      if (search) params.set('search', search);
      if (uptId) params.set('uptId', uptId);
      return api.get<DesignsResponse>(`/api/v1/designs?${params.toString()}`);
    },
  });

  // UPT filter dropdown is best-effort — we fetch the merchant's library
  // UPT distinct list lazily on mount. Failure is silent; the dropdown
  // simply collapses to "All Templates".
  const uptsQuery = useQuery({
    queryKey: ['portal', 'my-designs', 'upts'],
    queryFn: async () => {
      const all = await api.get<DesignsResponse>(
        `/api/v1/designs?context=MERCHANT_PRODUCT_LIBRARY&limit=100&offset=0`,
      );
      const map = new Map<string, PortalUpt>();
      for (const d of all.data) {
        if (d.upt && !map.has(d.upt.id)) {
          map.set(d.upt.id, d.upt);
        }
      }
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60_000,
  });

  const designs = designsQuery.data?.data ?? [];
  const total = designsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
  const currentPage = Math.floor(offset / PAGE_LIMIT) + 1;

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (search) c++;
    if (uptId) c++;
    return c;
  }, [search, uptId]);

  return (
    <div className="space-y-6">
      <Card padding="none">
        <div className="flex flex-col gap-4 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              My Designs
            </h2>
            <p className="text-sm text-text-secondary">
              Your reusable apparel designs. Spawn new products from any
              design without re-uploading artwork.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-tertiary">
              {total} {total === 1 ? 'design' : 'designs'}
              {activeFilterCount > 0 ? ` (${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'})` : ''}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-border px-5 py-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or ref id…"
              aria-label="Search designs"
            />
          </div>
          <div className="w-full sm:w-56">
            <Select
              value={uptId}
              onChange={(e) => setFilter('uptId', e.target.value)}
              aria-label="Filter by template"
              options={[
                { value: '', label: 'All Templates' },
                ...(uptsQuery.data ?? []).map((u) => ({
                  value: u.id,
                  label: u.name,
                })),
              ]}
            />
          </div>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                const next = new URLSearchParams();
                setSearchParams(next);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        {designsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : designsQuery.isError ? (
          <div className="py-16 text-center">
            <p className="text-sm text-danger mb-3">Failed to load designs.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => designsQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : designs.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm text-text-tertiary">
              {activeFilterCount > 0
                ? 'No designs match these filters.'
                : 'No designs yet. Start a new design from the catalog to populate your library.'}
            </p>
            {activeFilterCount === 0 && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => navigate('/catalog')}
              >
                Browse catalog
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {designs.map((d) => (
              <DesignCard
                key={d.id}
                design={d}
                onOpen={() => navigate(`/my-designs/${d.id}`)}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-text-tertiary">
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() =>
                  setFilter('offset', String(Math.max(0, offset - PAGE_LIMIT)))
                }
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + PAGE_LIMIT >= total}
                onClick={() => setFilter('offset', String(offset + PAGE_LIMIT))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function DesignCard({
  design,
  onOpen,
}: {
  design: LibraryDesign;
  onOpen: () => void;
}) {
  const productCount = design.spawnSummary?.productCount ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-2 rounded-lg border border-border bg-surface p-3 text-left transition-colors hover:border-brand-300 hover:bg-surface-secondary"
    >
      <div className="relative aspect-square overflow-hidden rounded border border-border bg-surface-secondary">
        {design.previewUrl ? (
          <img
            src={design.previewUrl}
            alt={design.title ?? design.designRefId ?? 'Design preview'}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[11px] text-text-tertiary">
            Preview pending
          </div>
        )}
        <div className="absolute top-1.5 right-1.5 flex gap-1">
          {productCount > 0 && (
            <Badge variant="info" size="sm">
              {productCount} product{productCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text-primary">
          {design.title ?? 'Untitled design'}
        </p>
        <p className="font-mono text-[11px] text-text-tertiary truncate">
          {design.designRefId ?? '—'}
        </p>
        {design.upt && (
          <p className="mt-0.5 truncate text-[11px] text-text-tertiary">
            {design.upt.name}
          </p>
        )}
      </div>
      <p className="text-[11px] text-text-tertiary">
        Updated {new Date(design.updatedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
    </button>
  );
}
